import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../../store';
import { setCurrentEditingCR, resetReviewCR } from '../../../store/slices/reviewCRSlice';
import { updateConstructionReview, deleteConstructionReview } from '../../../store/slices/databaseSlice';
import { selectCurrentEditingCR } from '../../../store/selectors/reviewCRSelectors';
import { constructionReviewsService, ConstructionReview } from '../../../services/construction-reviews.service';
import { CRList } from './CRList';
import { CREditView } from './CREditView';

export const CRReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Redux state
  const selectedCR = useAppSelector(selectCurrentEditingCR);
  
  // Local state
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // If we have an ID in the URL, load that specific CR
  useEffect(() => {
    if (id) {
      loadCR(id);
    } else {
      dispatch(setCurrentEditingCR(null));
    }
  }, [id, dispatch]);
  
  // Clear selected CR on unmount
  useEffect(() => {
    return () => {
      dispatch(resetReviewCR());
    };
  }, [dispatch]);

  const loadCR = async (crId: string) => {
    setLoading(true);
    try {
      const cr = await constructionReviewsService.getById(crId);
      dispatch(setCurrentEditingCR(cr));
    } catch (error) {
      console.error('Failed to load CR:', error);
      toast.error('Failed to load Construction Review');
      navigate('/review'); // Go back to list
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCR = (cr: ConstructionReview) => {
    navigate(`/review/${cr.id}`);
  };

  const handleBackToList = () => {
    navigate('/review');
  };

  const handleCRUpdate = (updatedCR: ConstructionReview) => {
    dispatch(setCurrentEditingCR(updatedCR));
    dispatch(updateConstructionReview(updatedCR));
  };

  const handleCRDelete = () => {
    if (selectedCR) {
      dispatch(deleteConstructionReview(selectedCR.id));
    }
    navigate('/review');
    // Toast is shown by CREditView component
  };

  return (
    <div className="h-[calc(100vh-64px)] overflow-hidden">
      <div className="mx-[9%] my-4 h-[calc(100%-2rem)] flex flex-col">
        {/* Full Width Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 border-b-0 rounded-t-3xl flex items-center gap-4">
          {selectedCR ? (
            <>
              <button
                onClick={handleBackToList}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] xl:text-xs 3xl:text-sm font-medium text-slate-500 hover:text-slate-800 hover:bg-white/60 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to List
              </button>
              <div className="w-px h-6 bg-orange-200/60" />
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xs xl:text-sm 3xl:text-lg font-bold text-slate-800 tracking-wide">{selectedCR.task_name}</h1>
                <p className="text-[9px] xl:text-[10px] 3xl:text-xs text-slate-400 font-mono">{selectedCR.task_id}</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 tracking-wide">Review CRs</h1>
                <p className="text-xs text-orange-700">Browse and edit construction reviews</p>
              </div>
            </>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-white/80 rounded-b-3xl shadow-sm border border-t-0 border-slate-200/60 overflow-auto p-4">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-full min-h-[40vh]">
              <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-slate-300 border-t-blue-500 mb-3"></div>
              <p className="text-slate-500 text-sm font-medium">Loading...</p>
            </div>
          ) : selectedCR ? (
            <CREditView
              cr={selectedCR}
              onUpdate={handleCRUpdate}
              onDelete={handleCRDelete}
            />
          ) : (
            <CRList
              onSelectCR={handleSelectCR}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
